import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';

interface EmailListLayoutToggleProps {
  threadedView: boolean;
  onChange: (threadedView: boolean) => void;
  /** Hide text labels on very narrow toolbars */
  compact?: boolean;
}

function FlatIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h10" />
    </svg>
  );
}

function ThreadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1 1 0 01-1-1v-3M3 8h2a2 2 0 012 2v6a2 2 0 01-2 2H5v4l-4-4H3a1 1 0 01-1-1v-3"
      />
    </svg>
  );
}

const EmailListLayoutToggle: React.FC<EmailListLayoutToggleProps> = ({
  threadedView,
  onChange,
  compact = false,
}) => {
  const { colors } = useTheme();

  const segmentClass = (active: boolean) =>
    cn(
      'inline-flex items-center justify-center gap-1.5 h-full px-2 sm:px-2.5 rounded-md text-xs font-medium transition-colors',
      active
        ? 'bg-indigo-600 text-white shadow-sm'
        : cn(colors.textMuted, colors.interactiveHover, 'hover:text-indigo-600 dark:hover:text-indigo-400'),
    );

  return (
    <div
      role="group"
      aria-label="List layout"
        className={cn(
          'inline-flex h-8 min-h-8 flex-shrink-0 items-stretch rounded-lg border p-0.5',
          colors.border,
          colors.surfaceHover,
        )}
    >
      <button
        type="button"
        onClick={() => onChange(false)}
        className={segmentClass(!threadedView)}
        aria-pressed={!threadedView}
        title="Flat view — one row per message"
      >
        <FlatIcon className="h-3.5 w-3.5 shrink-0" />
        {!compact && <span className="hidden sm:inline">Flat</span>}
      </button>
      <button
        type="button"
        onClick={() => onChange(true)}
        className={segmentClass(threadedView)}
        aria-pressed={threadedView}
        title="Thread view — group conversations"
      >
        <ThreadIcon className="h-3.5 w-3.5 shrink-0" />
        {!compact && <span className="hidden sm:inline">Thread</span>}
      </button>
    </div>
  );
};

export default EmailListLayoutToggle;
